import { CacheManager } from '@/lib/cache';
import ResearchServices from '@/services/research-services';
import userServices from '@/services/users-services';
import type { NativeMessageDTO } from '@/types/history-types';
import {
  type EntityState,
  type PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  current,
} from '@reduxjs/toolkit';

// Define types for history items
interface HistorySession {
  id: string; // Entity adapter requires 'id' field - using session_id as the unique identifier
  sess_id: number; // This is the numerical unique ID, e.g., 1959
  session_id: string; // This will store the string session_id, e.g., "1747-8224-8417-3232" (same as id)
  title: string;
  timestamp: number;
  isActive: boolean;
  messagesFetched: boolean; // Added flag
  access_role: 'owner' | 'shared_direct' | 'shared_group';
  /**
   * Group chat (#2607): the caller's access level on this session. 'read_write'
   * (owner, or a member granted write) can send messages; 'read' viewers get a
   * disabled input. Owned sessions are always 'read_write'.
   */
  permission: 'read' | 'read_write';
  /** Number of active share grants for owned sessions (0 = not shared). */
  share_count: number;
  /** Display name of the session owner for shared-with-me sessions. */
  owner_name: string | null;
  /** Organization ID if session belongs to an org, null for personal sessions. */
  org_id: string | null;
  /** Case (spis) ID this session was started from, null for general sessions (#6272). */
  case_id: number | null;
}

/**
 * Build a `HistorySession` for a freshly-created session, for an optimistic
 * Redux insert so the new chat appears in the sidebar immediately (without a
 * reload/refetch). Centralizes the "newly created" defaults so every
 * session-creation call site constructs the identical shape — #6272 missed the
 * Research.tsx call site once and left main's typecheck red; a single factory
 * prevents that drift recurring.
 *
 * NB: the slice holds ONE shared session entity bag with no per-type
 * discriminator, and `fetchSessionsHistory` does a wholesale `setAll` per list
 * type — so this optimistic insert is only correct when the currently-loaded
 * list matches the new session's type. Every caller below is a 'chat' session
 * (matches the chat sidebar); document_analysis/document_generation creation
 * paths must not use this against the chat list.
 */
export function buildNewlyCreatedHistorySession(params: {
  sessionId: string;
  title: string;
  orgId?: string | null;
  caseId?: number | null;
}): HistorySession {
  const { sessionId, title, orgId = null, caseId = null } = params;
  return {
    id: sessionId,
    sess_id: 0, // Numerical id unknown client-side; backend fills the real value.
    session_id: sessionId,
    title: title.length > 50 ? `${title.substring(0, 50)}…` : title,
    timestamp: Date.now(),
    isActive: true,
    messagesFetched: false,
    access_role: 'owner',
    // Owner of a freshly-created session always has full write access.
    permission: 'read_write',
    share_count: 0,
    owner_name: null,
    org_id: orgId,
    case_id: caseId,
  };
}

// Create entity adapter for history sessions with normalized state
// Entity adapter uses 'id' field by default, so no selectId needed
const historySessionsAdapter = createEntityAdapter<HistorySession>({
  sortComparer: (a, b) => b.timestamp - a.timestamp, // Most recent first
});

export type SessionListType =
  | 'chat'
  | 'search'
  | 'document_analysis'
  | 'document_generation';

interface PaginationState {
  hasMore: boolean;
  nextCursor: number | null;
  isLoadingMore: boolean;
  loadMoreError: string | null;
}

const defaultPagination: PaginationState = {
  hasMore: false,
  nextCursor: null,
  isLoadingMore: false,
  loadMoreError: null,
};

interface HistoryState {
  sessions: EntityState<HistorySession, string>; // Changed to EntityState
  /**
   * Sessions inserted optimistically by `addHistorySession` that no server list
   * has confirmed yet, keyed by session id. `fetchSessionsHistory.fulfilled`
   * replaces the whole list with a snapshot the server took when the request
   * STARTED; a session created while that request was in flight is missing
   * from it. Without this set the late snapshot wiped the new session, and
   * ResearchSession's "session deleted → redirect to /chat" guard fired on the
   * user's brand-new chat, stranding the answer (release v1.7.251 browser E2E,
   * GREETING/THANKS: POST /conversation/history took 4.9 s and resolved after
   * the session page mounted). An id leaves the set when a server list contains
   * it, or when the session is removed.
   */
  pendingCreatedSessions: Record<string, HistorySession>;
  isLoading: boolean;
  error: string | null;
  isFetched: boolean; // Flag to track if sessions have been fetched
  generatingTitleForSessionId: string | null; // New state for loading indicator
  pagination: Record<SessionListType, PaginationState>;
}

// Define payload type for updating session title/status
interface UpdateSessionPayload {
  sessionId: string;
  lastQueryMessageContent: string;
}

// Payload for directly updating title
interface UpdateSessionTitleOnlyPayload {
  sessionId: string;
  title: string;
}

// Initialize with empty state
const initialState: HistoryState = {
  sessions: historySessionsAdapter.getInitialState(), // Use adapter's initial state
  pendingCreatedSessions: {},
  isLoading: false,
  error: null,
  isFetched: false,
  generatingTitleForSessionId: null, // Initialize new state
  pagination: {
    chat: { ...defaultPagination },
    search: { ...defaultPagination },
    document_analysis: { ...defaultPagination },
    document_generation: { ...defaultPagination },
  },
};

// ========================================
// Session List Caching Infrastructure
// ========================================
/**
 * In-memory cache for session list to prevent redundant API calls
 * Key: user_id
 * Value: HistorySession[]
 *
 * Cache is automatically cleared on page refresh/navigation
 * TTL: 2 minutes (session list changes less frequently than chat history)
 */
const sessionListCache = new CacheManager<HistorySession[]>(
  'SessionList',
  2 * 60 * 1000 // 2 minutes TTL
);

/**
 * Clear all cached session lists. Call on user logout.
 */
/**
 * Bumped on every cache clear. A `fetchSessionsHistory` request captures it at
 * start and writes its result to the cache only if no clear happened while it
 * was in flight — otherwise a snapshot taken before a session was added or
 * removed would be cached for the full TTL and replayed on the next visit.
 */
let sessionListCacheGeneration = 0;

function clearAllSessionListCache(): void {
  sessionListCache.clear();
  sessionListCacheGeneration += 1;
}

const PAGE_SIZE = 30;

type SessionApiItem = {
  session_id: string;
  created_at: string;
  title: string | null;
  sess_id: number;
  access_role?: 'owner' | 'shared_direct' | 'shared_group';
  permission?: 'read' | 'read_write';
  share_count?: number;
  owner_name?: string | null;
  org_id?: string | null;
  case_id?: number | null;
};

function mapSessionItems(items: SessionApiItem[]): HistorySession[] {
  return items.map((item) => ({
    id: item.session_id,
    sess_id: item.sess_id,
    session_id: item.session_id,
    title: item.title || `Research session ${item.session_id}`,
    timestamp: new Date(item.created_at).getTime(),
    isActive: false,
    messagesFetched: false,
    access_role: item.access_role ?? 'owner',
    // Owned sessions always have full write. A shared/group row with a
    // missing permission field must fail CLOSED (read), matching the
    // backend's own default for a grant that omits permission
    // (session_shares.permission defaults to 'read', #8113) — defaulting to
    // 'read_write' here would invert that and briefly show a live composer
    // to a read-only member during a rolling deploy or the 2-min session
    // cache window (peer review, PR #8117).
    permission:
      item.permission ??
      ((item.access_role ?? 'owner') === 'owner' ? 'read_write' : 'read'),
    share_count: item.share_count ?? 0,
    owner_name: item.owner_name ?? null,
    org_id: item.org_id ?? null,
    case_id: item.case_id ?? null,
  }));
}

// Async thunk action to fetch session history (first page)
// User ID is determined server-side from the Supabase Auth session.
export const fetchSessionsHistory = createAsyncThunk(
  'history/fetchSessionsHistory',
  async (type: SessionListType | undefined, { rejectWithValue }) => {
    // Default to the chat list so existing no-arg callers are unaffected.
    const listType: SessionListType = type ?? 'chat';
    // Cache per session type — each nav section lists its own sessions, so a
    // single key would let one section serve another section's cached list.
    const cacheKey = `current_user:${listType}`;
    try {
      // Check cache before making API call (CacheManager handles TTL internally)
      const cached = sessionListCache.get(cacheKey);
      if (cached) {
        // fromCache=true tells the reducer to skip overwriting pagination state —
        // the cache stores only sessions, not hasMore/nextCursor, so rewriting
        // pagination to {hasMore:false} on a section re-visit would hide the
        // load-more button for a list that genuinely has more pages.
        return {
          sessions: cached,
          listType,
          hasMore: false,
          nextCursor: null,
          fromCache: true,
        };
      }

      const generationAtStart = sessionListCacheGeneration;
      const response = await ResearchServices.getSessionsHistory(listType, {
        limit: PAGE_SIZE,
      });
      const sessions = mapSessionItems(response.data);
      const hasMore: boolean = (response.meta?.['has_more'] ??
        false) as boolean;
      const nextCursor: number | null = (response.meta?.['next_cursor'] ??
        null) as number | null;

      if (sessionListCacheGeneration === generationAtStart) {
        sessionListCache.set(cacheKey, sessions);
      }

      return { sessions, listType, hasMore, nextCursor, fromCache: false };
    } catch (error) {
      return rejectWithValue(`Failed to fetch sessions history ${error}`);
    }
  }
);

// Async thunk to load additional pages (scroll-triggered)
export const fetchMoreSessionsHistory = createAsyncThunk(
  'history/fetchMoreSessionsHistory',
  async (
    { type, cursor }: { type: SessionListType; cursor: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await ResearchServices.getSessionsHistory(type, {
        limit: PAGE_SIZE,
        cursor,
      });
      const sessions = mapSessionItems(response.data);
      return {
        sessions,
        listType: type,
        hasMore: (response.meta?.['has_more'] ?? false) as boolean,
        nextCursor: (response.meta?.['next_cursor'] ?? null) as number | null,
      };
    } catch (error) {
      return rejectWithValue(`Failed to load more sessions ${error}`);
    }
  }
);

// Bulk-delete chat sessions (the /chats page "select + delete" action). There
// is no bulk endpoint, so this fans out the existing per-session DELETE
// (userServices.deleteUserHistory) and removes each succeeded row from the
// store. allSettled (not all) so one failure doesn't abort the rest; the
// rejected ids are returned for the caller to surface.
export const deleteChatSessionsBulk = createAsyncThunk(
  'history/deleteChatSessionsBulk',
  async (
    sessions: ReadonlyArray<{ sessId: number; sessionId: string }>,
    { dispatch }
  ) => {
    const results = await Promise.allSettled(
      sessions.map(async ({ sessId, sessionId }) => {
        const res = await userServices.deleteUserHistory(
          'chat',
          String(sessId)
        );
        if (res.status !== 'success') {
          throw new Error(res.message ?? 'delete failed');
        }
        return sessionId;
      })
    );
    const deletedIds: string[] = [];
    const failedIds: string[] = [];
    results.forEach((r, i) => {
      const sessionId = sessions[i]!.sessionId;
      if (r.status === 'fulfilled') {
        deletedIds.push(sessionId);
        // Drop the deleted session from the store (also invalidates the cache).
        dispatch(removeSessionById(sessionId));
      } else {
        failedIds.push(sessionId);
      }
    });
    return { deletedIds, failedIds };
  }
);

// Async thunk to fetch session messages
export const fetchSessionMessages = createAsyncThunk(
  'history/fetchSessionMessages',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await ResearchServices.getSessionHistory(sessionId);
      return {
        sessionId,
        messages: (response.data || []) as NativeMessageDTO[],
      };
    } catch (error) {
      return rejectWithValue(
        `Failed to fetch messages for session ${sessionId} ${error}`
      );
    }
  }
);

/**
 * Replace the list with a server snapshot. For the chat list
 * (`restorePending`), drop every pending optimistic session the snapshot now
 * contains (its row is authoritative) and re-insert the rest: they were created
 * after the server took the snapshot. See `pendingCreatedSessions`.
 */
function replaceWithServerList(
  state: HistoryState,
  serverSessions: HistorySession[],
  restorePending: boolean
): void {
  // Carry the pending sessions as they are NOW (title / isActive may have been
  // updated since the optimistic insert), not as they were inserted.
  const carried = Object.values(state.pendingCreatedSessions).map((pending) => {
    const live = state.sessions.entities[pending.id];
    return live ? current(live) : pending;
  });
  historySessionsAdapter.setAll(state.sessions, serverSessions);
  if (!restorePending) {
    return;
  }
  const serverIds = new Set(serverSessions.map((session) => session.id));
  for (const session of carried) {
    if (serverIds.has(session.id)) {
      delete state.pendingCreatedSessions[session.id];
    } else {
      historySessionsAdapter.addOne(state.sessions, session);
    }
  }
}

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    setHistorySessions: (state, action: PayloadAction<HistorySession[]>) => {
      historySessionsAdapter.setAll(state.sessions, action.payload);
    },
    addHistorySession: (state, action: PayloadAction<HistorySession>) => {
      const newSession = action.payload;
      if (!newSession.session_id && newSession.sess_id) {
        // Fallback logic or warning if session_id (string) is missing
      }
      historySessionsAdapter.addOne(state.sessions, newSession);
      state.pendingCreatedSessions[newSession.id] = newSession;
      // Invalidate all session list caches when a new session is added
      clearAllSessionListCache();
    },
    updateHistorySession: (
      state,
      action: PayloadAction<UpdateSessionPayload>
    ) => {
      const { sessionId, lastQueryMessageContent } = action.payload;
      const currentSession = state.sessions.entities[sessionId];

      if (currentSession) {
        // Calculate potential new title
        const newTitle = lastQueryMessageContent;

        // Check if title changed or session needs activation
        const needsUpdate =
          newTitle !== currentSession.title || !currentSession.isActive;

        if (needsUpdate) {
          historySessionsAdapter.updateOne(state.sessions, {
            id: sessionId, // sessionId is the same as id
            changes: {
              title: newTitle,
              isActive: true, // Ensure it's marked active
              // timestamp and messagesFetched remain unchanged
            },
          });
        }
      }
    },
    setActiveSession: (state, action: PayloadAction<string>) => {
      // action.payload here is the session_id (same as id)
      const updates = state.sessions.ids.map((id) => ({
        id,
        changes: { isActive: id === action.payload },
      }));
      historySessionsAdapter.updateMany(state.sessions, updates);
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearHistory: (state) => {
      historySessionsAdapter.removeAll(state.sessions);
      state.pendingCreatedSessions = {};
    },
    updateSessionTitleOnly: (
      state,
      action: PayloadAction<UpdateSessionTitleOnlyPayload>
    ) => {
      const { sessionId, title } = action.payload; // sessionId here is expected to be the string session_id
      historySessionsAdapter.updateOne(state.sessions, {
        id: sessionId,
        changes: { title },
      });
      // Invalidate cache so next fetchSessionsHistory picks up the new title from DB
      clearAllSessionListCache();
    },
    setGeneratingTitleStatus: (state, action: PayloadAction<string | null>) => {
      // action.payload is the string session_id
      state.generatingTitleForSessionId = action.payload;
    },
    removeSessionById: (state, action: PayloadAction<string>) => {
      // action.payload is the string session_id
      historySessionsAdapter.removeOne(state.sessions, action.payload);
      delete state.pendingCreatedSessions[action.payload];
      // Invalidate all session list caches when a session is removed
      clearAllSessionListCache();
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchSessionsHistory (first page — replaces entire list)
      .addCase(fetchSessionsHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSessionsHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optimistic inserts are chat sessions (see buildNewlyCreatedHistorySession),
        // so only the chat list confirms or restores them.
        replaceWithServerList(
          state,
          action.payload.sessions,
          action.payload.listType === 'chat'
        );
        state.isFetched = true;
        // On a cache hit we don't have fresh pagination metadata — skip
        // overwriting pagination so the existing hasMore/nextCursor are preserved.
        if (!action.payload.fromCache) {
          const t = action.payload.listType;
          state.pagination[t] = {
            hasMore: action.payload.hasMore,
            nextCursor: action.payload.nextCursor,
            isLoadingMore: false,
            loadMoreError: null,
          };
        }
      })
      .addCase(fetchSessionsHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle fetchMoreSessionsHistory (additional pages — appended)
      .addCase(fetchMoreSessionsHistory.pending, (state, action) => {
        const t = action.meta.arg.type;
        state.pagination[t].isLoadingMore = true;
        state.pagination[t].loadMoreError = null;
      })
      .addCase(fetchMoreSessionsHistory.fulfilled, (state, action) => {
        const t = action.payload.listType;
        historySessionsAdapter.upsertMany(
          state.sessions,
          action.payload.sessions
        );
        state.pagination[t] = {
          hasMore: action.payload.hasMore,
          nextCursor: action.payload.nextCursor,
          isLoadingMore: false,
          loadMoreError: null,
        };
      })
      .addCase(fetchMoreSessionsHistory.rejected, (state, action) => {
        const t = action.meta.arg.type;
        state.pagination[t].isLoadingMore = false;
        state.pagination[t].loadMoreError = action.payload as string;
      })
      // Handle fetchSessionMessages - this will update the title and preview
      // when we get the actual messages
      .addCase(fetchSessionMessages.fulfilled, (state, action) => {
        const { sessionId, messages } = action.payload; // sessionId here is the string session_id
        const currentSession = state.sessions.entities[sessionId];

        if (currentSession) {
          // Only update title from message content if it's the default placeholder title
          const currentTitle = currentSession.title;
          // Default title was using item.session_id which is now session.session_id
          const defaultTitleToCheck = `Research session ${currentSession.session_id}`;
          const isDefaultTitle =
            currentTitle === defaultTitleToCheck || !currentTitle;

          const changes: Partial<HistorySession> = {
            messagesFetched: true,
          };

          if (
            Array.isArray(messages) &&
            messages.length > 0 &&
            isDefaultTitle
          ) {
            const userMessage = messages.find(
              (msg) => msg.role === 'user' && msg.content
            );

            if (userMessage?.content) {
              changes.title = userMessage.content;
            }
          }

          historySessionsAdapter.updateOne(state.sessions, {
            id: sessionId,
            changes,
          });
        }
      });
  },
});

// Export actions
export const {
  addHistorySession,

  setActiveSession,

  updateSessionTitleOnly,
  setGeneratingTitleStatus,
  removeSessionById,
} = historySlice.actions;

// Export adapter selectors for use in components
export const historySessionsSelectors = historySessionsAdapter.getSelectors<{
  history: HistoryState;
}>((state) => state.history.sessions);

// Selectors that separate owned sessions from sessions shared with the user
const selectAllSessions = (state: { history: HistoryState }) =>
  historySessionsSelectors.selectAll(state);

// Sessions shared directly with the user (personal grant)
export const selectDirectSharedSessions = createSelector(
  selectAllSessions,
  (sessions) => sessions.filter((s) => s.access_role === 'shared_direct')
);

// Sessions shared with the user via an org group they belong to
export const selectGroupSharedSessions = createSelector(
  selectAllSessions,
  (sessions) => sessions.filter((s) => s.access_role === 'shared_group')
);

export const selectHistoryPagination =
  (type: SessionListType) => (state: { history: HistoryState }) =>
    state.history.pagination[type];

export default historySlice.reducer;
